import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      [elemName: string]: any;
    }
  }
}

export interface WebComponentProps {
  [key: string]: any;
}

export interface WebComponentRef {
  [key: string]: any;
}

export const createWebComponentWrapper = (tagName: string) => {
  return forwardRef<WebComponentRef, WebComponentProps>((props, ref) => {
    const elementRef = useRef<HTMLElement>(null);

    useImperativeHandle(ref, () => ({
      getElement: () => elementRef.current,
      ...elementRef.current
    }));

    useEffect(() => {
      const element = elementRef.current;
      if (!element) return;

      Object.keys(props).forEach(key => {
        if (key !== 'children' && key !== 'ref') {
          if (typeof props[key] === 'object') {
            element.setAttribute(key, JSON.stringify(props[key]));
          } else {
            element.setAttribute(key, String(props[key]));
          }
        }
      });

      const eventListeners: { [key: string]: (event: Event) => void } = {};
      Object.keys(props).forEach(key => {
        if (key.startsWith('on') && typeof props[key] === 'function') {
          const eventName = key.toLowerCase().replace('on', '');
          eventListeners[eventName] = props[key];
          
          // Add debugging for event registration
          console.log(`🔧 WebComponent: Registering event listener for '${eventName}'`);
          
          element.addEventListener(eventName, props[key]);
          
          // Also try to listen for the event as a custom event
          if (eventName === 'continuebtnclick') {
            console.log('🔧 WebComponent: Also listening for continueBtnClick as custom event');
            element.addEventListener('continueBtnClick', props[key] as EventListener);
          }
        }
      });

      // Debug: Listen for all events to see what's happening
      const debugAllEvents = (event: Event) => {
        console.log('🔧 WebComponent Debug - Event detected:', {
          type: event.type,
          target: event.target,
          currentTarget: event.currentTarget,
          detail: (event as CustomEvent).detail,
          event: event
        });
      };

      // Listen for all events for debugging
      element.addEventListener('*', debugAllEvents as EventListener);

      return () => {
        Object.keys(eventListeners).forEach(eventName => {
          element.removeEventListener(eventName, eventListeners[eventName]);
        });
        element.removeEventListener('*', debugAllEvents as EventListener);
      };
    }, [props]);

    return React.createElement(tagName, {
      ref: elementRef,
      ...props
    });
  });
};

export const registerAngularWebComponent = (
  componentClass: any,
  tagName: string
) => {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, componentClass);
  }
};

export const loadAngularWebComponent = async (scriptUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${scriptUrl}`));
    document.head.appendChild(script);
  });
}; 